import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
// import { WrapBridgeMappings } from "@constants/common";
import BaseImage from "@components/BaseImage";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Typography,
} from "@kelp-dao/kernel-design-system";
import { BW_VARIANT, TBW_VARIANT } from "./types";

type LearnMore = {
  question?: string;
  answer?: string;
  iconChips?: { icon: string; label: string }[];
};

const learnMoreDataAgEth: LearnMore[] = [
  {
    question: "What is bridging?",
    answer:
      "Bridging is the process of moving tokens from one blockchain to another.",
  },
  {
    question: "What are wrapped assets?",
    answer:
      "WraWrapped tokens are digital assets that mirror the value of another cryptocurrency from a different blockchain. They are created to enhance interoperability between different blockchains, allowing assets from one blockchain to be used on another.",
  },
  {
    question: "What are agETH & agETH.axl?",
    answer:
      "agETH is the receipt token for Gain's Airdrop Vault. agETH.axl is the wrapped token for agETH, used on L2s where agETH is not yet available.",
  },
  {
    question: "Where can I use agETH.axl?",
    answer: "agETH.axl is currently live on three L2 networks.",
    iconChips: [
      {
        icon: "/assets/chains/linea.svg",
        label: "Linea",
      },
      {
        icon: "/assets/chains/scroll.svg",
        label: "Scroll",
      },
    ],
  },
];

const learnMoreDataRsEth: LearnMore[] = [
  {
    question: "What is bridging?",
    answer:
      "Bridging is the process of moving tokens from one blockchain to another.",
  },
  {
    question: "What are wrapped assets?",
    answer:
      "Wrapped tokens are digital assets that mirror the value of another cryptocurrency from a different blockchain. They are created to enhance interoperability between different blockchains, allowing assets from one blockchain to be used on another.",
  },
  {
    question: "What is wrsETH?",
    answer:
      "wrsETH is the wrapped token for rsETH, Kelp’s Liquid Staked Token. wrsETH is use on L2s where rsETH is not available natively.",
  },
];

type TLearnMore = {
  variant?: TBW_VARIANT;
};

const LearnMoreV2 = forwardRef((props: TLearnMore, ref) => {
  const { variant } = props;
  const [isOpen, setIsOpen] = useState(false);
  const accordionRef = useRef<HTMLDivElement>(null);

  const handleLinkClick = () => {
    if (isOpen) {
      // If already open, just scroll immediately
      accordionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    } else {
      // If closed, open it (scroll will happen via useEffect)
      setIsOpen(true);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        accordionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 500);
    }
  }, [isOpen]);

  useImperativeHandle(ref, () => ({
    openAccordion: () => handleLinkClick(),
  }));

  const learnMoreData =
    variant === BW_VARIANT.AGETH ? learnMoreDataAgEth : learnMoreDataRsEth;

  const renderLearnMoreSection = (learnMore: LearnMore[]) => {
    return (
      <div className="flex flex-col gap-4">
        {learnMore.map((item, index) => (
          <div
            key={index}
            className={`flex flex-col ${item.iconChips ? "gap-4" : "gap-0"}`}
          >
            <div className="flex flex-col gap-0.5">
              {item.question && (
                <Typography
                  variant="xsmall"
                  type="body"
                  className={`text-text-primary ${
                    variant === BW_VARIANT.AGETH
                      ? "text-[10px] font-semibold"
                      : "text-xs font-normal"
                  }`}
                >
                  {item.question}
                </Typography>
              )}
              {item.answer && (
                <div className="flex flex-col gap-2">
                  <Typography
                    variant="helper"
                    as="span"
                    type="misc"
                    className="text-text-secondary"
                  >
                    {item.answer}
                  </Typography>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-[6px]">
              {item.iconChips &&
                item.iconChips.map((chip, index) => (
                  <div
                    key={index}
                    className="flex gap-2 items-center justify-center border border-border-default rounded-full px-1 py-1"
                  >
                    <BaseImage
                      src={chip.icon}
                      alt={chip.label}
                      width={16}
                      height={16}
                    />
                    <Typography
                      variant="xsmall"
                      type="body"
                      className="text-text-secondary"
                    >
                      {chip.label}
                    </Typography>
                  </div>
                ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div ref={accordionRef}>
      <Accordion
        type="single"
        collapsible
        value={isOpen ? "learn-more" : ""}
        onValueChange={(value) => setIsOpen(value === "learn-more")}
        className="w-full gap-10"
      >
        <AccordionItem value="learn-more" className="border-none">
          <AccordionTrigger
            className="hover:no-underline py-6 px-0 md:px-8 gap-10 border-t border-solid border-border-default rounded-none"
            iconClassName="text-button-secondary"
          >
            <div className="flex gap-1">
              <Typography
                variant="xsmall"
                type="body"
                className="text-text-secondary"
              >
                Learn more about using{" "}
                {variant === BW_VARIANT.AGETH ? "agETH" : "rsETH"} across chains
              </Typography>
            </div>
          </AccordionTrigger>

          <AccordionContent className="pb-3 lg:pb-6 px-0 md:px-8 pt-4">
            {renderLearnMoreSection(learnMoreData)}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
});

LearnMoreV2.displayName = "LearnMoreV2";

export default memo(LearnMoreV2);
